<template>
  <v-layout
    column
    class="pa-3 mt-1"
    :class="backgroundClass"
  >
    <v-layout
      align-center
      wrap
      justify-end
    >
      <!-- Label -->
      <v-layout
        v-if="field.data.label && !noLabel"
        class="field-label mb-1"
      >
        {{ field.data.label }}
      </v-layout>
      <!-- Pick default image or Branding preset image (if configured) -->
      <v-slide-y-transition>
        <v-menu
          v-if="!!presetImageFromBranding && !valueIsPresetImageFromBranding"
          offset-y
        >
          <v-btn
            slot="activator"
            ref="presetmenubutton"
            class="my-0 mr-0 grey--text text--darken-1"
            flat
            round
            small
          >
            <span class="caption">{{
              $t('fromBrandingAsPresetMenuButton')
            }}</span>
            <v-icon medium>
              arrow_drop_down
            </v-icon>
          </v-btn>
          <v-list class="list-tile-min-height list-tile-auto-height">
            <v-list-tile
              v-if="!!presetImageFromBranding"
              :title="$t('chooseImage')"
              @click="$emit('update:value', presetImageFromBranding)"
            >
              <v-img
                :src="presetImageFromBranding"
                min-width="140px"
                height="auto"
                contain
                class="my-2"
              />
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-slide-y-transition>
    </v-layout>
    <v-layout
      justify-center
      align-center
      :class="{clickable: !!value}"
      @click="onImageContainerClicked"
    >
      <v-progress-circular
        v-if="busy"
        indeterminate
        color="primary"
        size="50"
        width="6"
        style="position: absolute"
      />
      <img
        v-if="value"
        :src="imageFieldPreviewURL"
        class="imagefield-preview mb-1"
        :class="{
          opacity50: busy,
          prevent: makeItHarderToSaveImageViaRightclick,
        }"
        :style="field.data.css ? field.data.css.field : null"
        :title="$t('changeImageReplace')"
      >
      <v-btn
        v-else
        round
        flat
        large
        class="ma-0 grey--text text--darken-1"
        @click="onChooseImageButtonClicked"
      >
        <v-icon left>
          image
        </v-icon>
        {{ $t('chooseImage') }}
      </v-btn>
    </v-layout>
    <v-layout
      v-if="value"
      justify-start
      style="width: 100%"
      class="mt-2"
    >
      <!-- Replace -->
      <v-btn
        v-if="value"
        icon
        class="ml-0 my-0 grey--text text--darken-1"
        :title="$t('changeImageReplace')"
        @click="$emit('change-image', {field: field, value: value})"
      >
        <v-icon>image</v-icon>
      </v-btn>
      <!-- Recrop -->
      <v-slide-y-reverse-transition>
        <v-btn
          v-if="showImageRecropOption"
          icon
          class="ml-0 my-0 grey--text text--darken-1"
          :title="$t('changeImageRecrop')"
          @click="
            $emit('change-image', {field: field, value: value, recrop: true})
          "
        >
          <v-icon>crop</v-icon>
        </v-btn>
      </v-slide-y-reverse-transition>
      <!-- Clear -->
      <v-slide-y-reverse-transition>
        <v-btn
          v-if="value"
          icon
          class="ml-0 my-0 grey--text text--darken-1"
          :title="$t('clearImage')"
          @click="askClearImage()"
        >
          <v-icon style="transform: rotateZ(45deg)">
            add_circle
          </v-icon>
        </v-btn>
      </v-slide-y-reverse-transition>
      <!-- Extra buttons e.g. alternate background color -->
      <slot name="extraButtons" />
    </v-layout>
  </v-layout>
</template>

<script>
import {OverlayOptionsField} from '@/mixins/overlayOptions.js';

export default {
  props: {
    field: {
      type: OverlayOptionsField,
      required: true,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    backgroundClass: {
      type: String,
      default: 'overlay-options-field-bg',
    },
    makeItHarderToSaveImageViaRightclick: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      busy: false,
    };
  },
  computed: {
    lastBrandingApplied() {
      return this.$store.state.common.lastBrandingApplied;
    },
    /**
     * Show uploaded image preview instead of spritesheet, if there is one.
     *
     * We need the spritesheet for replacement of an image within Adobe
     * Animate output, but they are not good for previewing. Since we
     * keep the original next to the spritesheet file, we can construct
     * the URL to that file instead here.
     */
    imageFieldPreviewURL() {
      if (this.value.includes('.spritesheet.')) {
        return this.value.replace('.spritesheet.', '.');
      }
      return this.value;
    },
    defaultValue() {
      return this.field.data.default_value;
    },
    /**
     * IMAGE fields default to  'must be changed' unless specified otherwise.
     */
    defaultValueMustBeChanged() {
      return this.field.data.default_value_must_be_changed === undefined
        ? true
        : this.field.data.default_value_must_be_changed;
    },
    presetImageFromBranding() {
      if (
        !!this.field.data.from_branding_as_preset &&
        !!this.lastBrandingApplied
      ) {
        return this.lastBrandingApplied[
          this.field.data.from_branding_as_preset
        ];
      }
      return null;
    },
    valueIsPresetImageFromBranding() {
      return this.value === this.presetImageFromBranding;
    },
    showImageRecropOption() {
      if (!this.value) {
        return false;
      }
      return !this.defaultValueMustBeChanged || this.value != this.defaultValue;
    },
  },
  created() {
    this.$bus.$on('set-overlay-data-image-field-busy', (fieldName, busy) => {
      if (!this.field || this.field.name !== fieldName) {
        return;
      }
      this.busy = busy;
    });
  },
  methods: {
    onImageContainerClicked() {
      if (!this.value) {
        return;
      }

      this.$emit('change-image', {
        field: this.field,
        value: this.value,
        recrop: this.showImageRecropOption,
      });
    },
    onChooseImageButtonClicked() {
      this.$emit('change-image', {field: this.field, value: this.value});
    },
    askClearImage() {
      const confirmed = confirm(this.$t('clearImageQuestion'));
      if (!confirmed) {
        return;
      }
      this.$emit('update:value', '');
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 12px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.54);
}

.imagefield-preview {
  width: 144px;
  max-width: 144px;
  min-height: 81px;
  max-height: 144px;
  object-fit: contain;
}

.prevent {
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/OverlayDataImageField.vue